<template>
  <div>
    <section-title class="mt-4 mb-3" has-line title="Documento de Enmienda" />
    <div
      v-if="!documents.length && !isAdding"
      class="bg-secondary-3 p-3 mt-3 rounded"
    >
      <div>
        <div class="upload-file-button__container">
          <div class="panel-row-without-selector p-4">
            <div class="col-12 text-center">
              <button
                class="btn bg-light-white col-12 col-md-4"
                @click="isAdding = true"
              >
                <em class="fas fa-file-plus pr-4 text-green" />
                <span class="text-button-add-document">
                  Presione aquí para anejar documento
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <amendment-document-row
        v-for="document in documents"
        :key="document.documentId"
        :document="document"
        :has-delete-permission="hasDeleteDocumentPermission"
        @on-download="onDownload"
        @on-remove="onRemoveDocuments"
      />
    </div>
    <div v-if="isAdding" class="bg-secondary-3 p-3 mt-3 rounded">
      <validation-observer ref="documentObserver" class="row mx-2 " tag="div">
        <div class="col-md-4 col-sm-6">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              id="ddlDocumentType"
              v-model="model.documentType"
              custom-error-msg="Favor seleccionar tipo de documento."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Tipo de Documento"
              :options="documentTypes"
            />
          </validation-provider>
        </div>
        <div
          class="col-md-4 col-sm-6 "
          :class="model.file && 'd-flex justify-content-center'"
        >
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-file-input
              ref="fileUpload"
              v-model="model.file"
              :accept="allowedFiles"
              class="mt-4 pt-1"
              custom-error-msg="Favor seleccionar un documento."
              display-style="normal"
              :error="errors[0]"
              label=""
              label-position="left"
              :on-download="() => {}"
              required
            />
          </validation-provider>
        </div>
        <div class="col-md-4">
          <div class="mt-4 text-right">
            <action-button
              id="btnCancel"
              i="fas fa-times-circle"
              variant="outline-danger btn-cancel danger m-2"
              @click="closeAdding"
            >
              Cancelar
            </action-button>
            <action-button
              id="btnAdd"
              i="fas fa-file-plus"
              variant="success pl-4 pr-4"
              @click="onAdd"
            >
              Añadir
            </action-button>
          </div>
        </div>
      </validation-observer>
    </div>
    <div
      v-if="documents.length > 0 && !isAdding"
      class="d-flex justify-content-end mt-3"
    >
      <action-button
        v-if="hasAddDocumentPermission"
        i="fas fa-file-plus"
        variant="success"
        @click="isAdding = true"
      >
        Añadir
      </action-button>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomFileInput from 'CustomFileInput';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import { serialize as objectToFormData } from 'object-to-formdata';
import AmendmentDocumentRow from './AmendmentDocumentRow';
import {
  getAmendmentDocumentTypes as _getAmendmentDocumentTypes,
  uploadContractDocument,
  getContractDocuments,
  removeContractDocument,
} from '@/modules/provider/api';
import service from '@/modules/app/api';

import alert from '@/mixins/alert.mixin';
import DocumentTypes from '@/utils/constants/documentTypes';

export default {
  name: 'AmendmentDocument',
  components: {
    SectionTitle,
    CustomFileInput,
    CustomDropDown,
    ActionButton,
    AmendmentDocumentRow,
  },
  mixins: [alert],
  data: () => ({
    documentTypes: [],
    model: {
      documentType: null,
      file: null,
    },
    isAdding: false,
    allowedFiles: '.pdf',
    documents: [],
  }),
  computed: {
    contractId() {
      return this.$route.params.contractId;
    },
    hasAddDocumentPermission() {
      return this.userHasPermissions('provider.contract.document.add');
    },
    hasDeleteDocumentPermission() {
      return this.userHasPermissions('provider.contract.document.delete');
    },
  },
  mounted() {
    this.getAmendmentDocumentTypes();
    this.getContractDocuments();
  },
  methods: {
    async validate() {
      return await this.$refs.documentObserver.validate();
    },
    async onAdd() {
      const isValid = await this.validate();
      if (!isValid) return;

      const documents = [
        {
          documentTypeId: this.model.documentType.id,
          file: this.model.file,
        },
      ];

      const { data } = await uploadContractDocument(
        this.contractId,
        objectToFormData(
          { documents },
          { indices: true, nullsAsUndefineds: true }
        )
      ).catch((error) => {
        this.ShowToast('', error.response.data.message, 'error');
      });

      if (data) {
        this.closeAdding();
        this.ShowToast(
          'Proceso Exitoso',
          'El documento de enmienda se ha añadido exitosamente.',
          'success'
        );
      }

      await this.getContractDocuments();
    },
    async getAmendmentDocumentTypes() {
      try {
        const response = await _getAmendmentDocumentTypes();
        this.documentTypes = response.data.filter(
          (x) => x.id === DocumentTypes.EnmiendaDeContrato
        );

        if (this.documentTypes.length == 1) {
          this.model.documentType = { ...this.documentTypes[0] };
        }
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async getContractDocuments() {
      try {
        const response = await getContractDocuments(this.contractId);
        this.documents = response.data.filter(
          (x) => x.documentTypeId == DocumentTypes.EnmiendaDeContrato
        );
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async onDownload(documentId) {
      if (documentId != undefined) {
        service.downloadDocumentByAttachmentId(documentId).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }
    },
    async onRemoveDocuments(documentId) {
      try {
        const confirmation = await this.ShowCancelModal(
          'Confirmación',
          '¿Está seguro(a) que desea eliminar el documento seleccionado?',
          'Si',
          'No'
        );

        if (!confirmation) return;

        await removeContractDocument(documentId);
        this.ShowToast(
          'Proceso Exitoso',
          'Documento eliminado exitosamente',
          'success'
        );
      } catch (error) {
        this.ShowToast(
          'Error',
          'No ha sido posible eliminar el documento seleccionado.',
          'error'
        );
      }
      await this.getContractDocuments();
    },
    closeAdding() {
      this.isAdding = false;
      this.model.file = null;
      this.$refs.fileUpload.removeFile();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';
</style>
